/**
 * D-128 — PM web Clock skew admin page.
 *
 * Reads /api/admin/clock-skew?days=7; surfaces chronic-skew patterns so PM
 * can contact workers out-of-band ("your phone time is 35 min off"). Latin
 * digits for delta_seconds regardless of locale (numeric values not mirrored).
 */
import { FormattedMessage } from "react-intl";
import { useClockSkewAudit } from "../../hooks/useClockSkewAudit";
import { formatDateTime } from "../../lib/formatDateTime";

export function ClockSkewPage() {
  const { data: rows = [], isLoading, isError } = useClockSkewAudit(7);

  return (
    <div className="ps-8 pe-8 pbs-8 pbe-8" data-testid="clock-skew-page">
      <header className="flex items-center justify-between mbe-6">
        <h1 className="text-2xl font-semibold text-on-surface">
          <FormattedMessage id="LABEL_CLOCK_SKEW_TILE" />
        </h1>
      </header>
      {isLoading ? (
        <div data-testid="clock-skew-loading">…</div>
      ) : isError ? (
        <div data-testid="clock-skew-error">—</div>
      ) : rows.length === 0 ? (
        <div
          className="bg-surface-raised border border-divider rounded-md ps-6 pe-6 pbs-12 pbe-12 text-center text-text-secondary"
          data-testid="clock-skew-empty"
        >
          <FormattedMessage id="LABEL_CLOCK_SKEW_EMPTY" />
        </div>
      ) : (
        <table
          className="w-full bg-surface-raised border border-divider rounded-md overflow-hidden text-sm"
          data-testid="clock-skew-table"
        >
          <thead>
            <tr className="bg-surface border-be border-divider">
              <th className="ps-4 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_WORKER" />
              </th>
              <th className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_SKEW_SECONDS" />
              </th>
              <th className="ps-2 pe-2 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_REPORTED_TIME" />
              </th>
              <th className="ps-2 pe-4 pbs-3 pbe-3 text-start font-semibold text-on-surface">
                <FormattedMessage id="LABEL_COL_RECEIVED" />
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-be border-divider"
                data-testid={`clock-skew-row-${row.id}`}
              >
                <td className="ps-4 pe-2 pbs-3 pbe-3 font-medium text-on-surface">
                  {row.worker_name}
                </td>
                <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary">{row.delta_seconds}</td>
                {/*
                  Audit W4-010 — raw ISO is INTENTIONAL on this column. The
                  ClockSkew table is a forensic admin tile (CP3); the operator
                  needs to see exactly what the device claimed (microseconds,
                  zone, "Z" suffix included) so they can correlate against
                  the worker's wall-clock and the server-side ±5 min clamp.
                  Compare with the next column which DOES go through
                  formatDateTime() because that's the canonical server
                  timestamp the operator already mentally maps. Latin-digits
                  rule still holds because formatDateTime() and Postgres-side
                  ISO emission both use Latin numerals.
                */}
                <td className="ps-2 pe-2 pbs-3 pbe-3 text-text-secondary font-mono text-xs">
                  {row.raw_client_value_iso}
                </td>
                <td className="ps-2 pe-4 pbs-3 pbe-3 text-text-secondary">
                  {formatDateTime(row.server_received_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
